import { and, AnyStateConfig, assertEvent, DoneActorEvent, enqueueActions, setup } from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { devLog } from '../../utils/devLog';
import { getUserGuards, isStateGuard } from '../helpers';

import type { RendezvousFlowContext, TvCompleteOutput, TvOutput } from './types';
import { LEGACY_RENDEZVOUS_SIGN_IN_URL, RENDEZVOUS_LOCAL_STORAGE_KEY } from './types';

export type { ActivationScreen, RendezvousFlowContext, TvCompleteOutput, TvOutput } from './types';
export { LEGACY_RENDEZVOUS_SIGN_IN_URL, RENDEZVOUS_ACTIVATION_SUCCESS_STATE } from './types';

export const FALLBACK_DEFAULT_REGION_CODE = 'us';

export const rendezvousFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, getConfigByKey, queryClient, router, region } =
            rawParams as InitMachineParams;
        const isState = isStateGuard({ getConfigByKey, flowId: 'rendezvous', router, region });
        const userGuards = getUserGuards(queryClient);
        return setup({
            types: {
                events: {} as
                    | { type: 'xstate.init' }
                    | { type: 'POP_STATE' }
                    | { type: 'ROUTE_CHANGE' }
                    | { type: 'CLICK_SIGN_IN_CTA' }
                    | { type: 'CLICK_SIGN_UP_CTA' }
                    | { type: 'CLICK_MVPD_CTA' }
                    | { type: 'ALREADY_LOGGED_IN' }
                    | DoneActorEvent<TvOutput, 'tv'>
                    | DoneActorEvent<TvCompleteOutput, 'tvComplete'>,
                context: {} as RendezvousFlowContext,
            },
            guards: {
                isTv: and(isState('tv', 'equality')),
                isTvSelect: and(isState('tvSelect', 'inclusion')),
                isTvComplete: and(isState('tvComplete', 'inclusion')),
                activationDidSucceed: ({ event }) => {
                    if (event.type === 'xstate.done.actor.tvComplete') {
                        return !event.output.activationDidFail;
                    }

                    return false;
                },
                activationDidFail: ({ event }) => {
                    if (event.type === 'xstate.done.actor.tvComplete') {
                        return event.output.activationDidFail === true;
                    }

                    return false;
                },
                deviceAllowsSignUpOnWeb: ({ context, event }) => {
                    if (event.type === 'xstate.done.actor.tv') {
                        return event.output.activationScreen === 'SIGN_UP';
                    }

                    return context.activationScreen === 'SIGN_UP';
                },
                isAnonymousAndDeviceAllowsSignUpOnWeb: and([
                    'isAnonymous',
                    'deviceAllowsSignUpOnWeb',
                ]),
                isRegOrExSubAndDeviceAllowsSignUpOnWeb: and([
                    'isRegisteredOrExSubscriber',
                    'deviceAllowsSignUpOnWeb',
                ]),
                ...userGuards,
            },
            actions: {
                clearDeviceInfo: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.tvComplete');
                    enqueue.assign({
                        activationCode: '',
                        activationScreen: undefined,
                        device: undefined,
                    });
                    globalThis.localStorage?.removeItem(RENDEZVOUS_LOCAL_STORAGE_KEY);
                }),
                checkUserStatus: enqueueActions(({ enqueue, check }) => {
                    if (check({ type: 'isLoggedIn' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    }
                }),
                saveDeviceInfo: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, ['xstate.done.actor.tv', 'xstate.done.actor.tvComplete']);
                    devLog('[rendezvousFlow] saveDeviceInfo event.output', () => event.output);
                    enqueue.assign({
                        activationCode: event.output.activationCode,
                        activationScreen: event.output.activationScreen,
                        device: event.output.device,
                    });
                    if (event.type === 'xstate.done.actor.tv') {
                        try {
                            globalThis.localStorage?.setItem(
                                RENDEZVOUS_LOCAL_STORAGE_KEY,
                                JSON.stringify({
                                    activationCode: event.output.activationCode,
                                    activationScreen: event.output.activationScreen,
                                    deviceName: event.output.device?.name,
                                    devicePartner: event.output.device?.partner,
                                }),
                            );
                        } catch (e) {
                            console.error(
                                '[rendezvousFlow] error saving device info to localStorage',
                                e,
                            );
                        }
                    } else {
                        globalThis.localStorage?.removeItem(RENDEZVOUS_LOCAL_STORAGE_KEY);
                    }
                }),
                goToLegacySignIn: () => {
                    devLog('[rendezvousFlow] goToLegacySignIn');
                    window.location.assign(LEGACY_RENDEZVOUS_SIGN_IN_URL);
                },
                goToLegacySignUp: () => {
                    devLog('[rendezvousFlow] goToLegacySignUp');
                    window.location.assign('/account/user-flow/tn-rendezvous/');
                },
                goToLegacyMVPD: () => {
                    devLog('[rendezvousFlow] goToLegacyMVPD');
                    window.location.assign('/account/flow/tn-rendezvous/action/partner/');
                },
                waitThenGoToHomePage: () => {
                    setTimeout(() => {
                        window.location.assign('/home/');
                    }, 15000);
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: {
                defaultRegionCode: FALLBACK_DEFAULT_REGION_CODE,
                ...config.context, // overrides defaultRegionCode with value from rendezvous.jsonc blueprint
                activationCode: '',
                activationScreen: undefined,
            },
        });
    },
};
